<template>
  <!-- Public pages (sign-in, magic link, invitation): no product frame at all — a new client by magic link has no
       product hint yet and must not land in the old 480 px frame (05 §2.2). Chosen by the ROUTE, not by the session:
       switching frames on sign-in would remount the sign-in screen and flash the tab bar. The privacy page is the one
       public page a signed-in client reaches from the cabinet, so it keeps the cabinet frame for them. -->
  <PublicLayout v-if="usePublicLayout">
    <router-view />
  </PublicLayout>

  <!-- The client cabinet (vitrine): tab bar on a phone, a navigation rail on a tablet, a sidebar on a desktop. -->
  <CabinetShell v-else-if="isVitrine" :mode="layout.mode.value">
    <!-- The tabs stay alive between visits (owner, 02.11): «Назад» and a tab switch show what is already known at
         once and refresh in the background, instead of a spinner on every visit. Keyed by the client, so another
         session never sees this one's screens. -->
    <router-view v-slot="{ Component }">
      <keep-alive :key="auth.customerId ?? 'anon'" :include="keptTabs">
        <component :is="Component" />
      </keep-alive>
    </router-view>
    <template v-if="auth.isAuthenticated" #nav>
      <CabinetNav :items="cabinetNav" :current="cabinetCurrent" :mode="layout.mode.value" @select="onCabinetSelect" />
    </template>
  </CabinetShell>

  <AppShell v-else>
    <!-- In vitrine each screen renders its own header (greeting / back) — a shell header would
         duplicate it (two circles at top-left), so we don't show it in vitrine. -->
    <template v-if="auth.isAuthenticated && !isVitrine" #header>
      <TopHeader
        :title="headerTitle"
        :subtitle="headerSubtitle"
        :name="auth.customerId ?? undefined"
        :on-profile="goSettings"
      />
    </template>

    <router-view />

    <template v-if="auth.isAuthenticated" #nav>
      <BottomTabBar :tabs="tabs" :current="currentTab" @change="onTabChange" />
    </template>
  </AppShell>

  <!-- Keyed by the shell: its sheet teleports into the shell's own #sheet-portal, and a magic-link entry starts under
       PublicLayout, then swaps in CabinetShell with a new portal. Unkeyed, the sheet stayed bound to the removed portal
       and the timed install prompt opened into nothing (install-sheet-ios E2E, 01.11). -->
  <InstallPwaSheet
    :key="shellKey"
    :open="install.sheetVisible.value"
    :is-ios="install.isIos.value"
    @close="onInstallClose"
    @install="onInstallTrigger"
  />

  <!--
    Per Round 21 H5 — global Support FAB + Drawer.
    Mounted once at the shell level so it's reachable from any screen (Home/Payments/History/Settings
    in the full PWA + card/history/settings in vitrine). Conditionally rendered only when authed.
    Hidden on public routes (login/register/magic) via {meta.public}.
  -->
  <template v-if="auth.isAuthenticated && !routeIsPublic">
    <!-- In vitrine the chat is reachable via the header manager-chip + the Transfers tab, so we don't
         show the FAB (the target vitrine prototype has none). In full mode the FAB stays. -->
    <button v-if="!isVitrine" class="app-support-fab" :aria-label="t('support.title')" @click="supportOpen = true">
      <span aria-hidden="true">?</span>
      <!-- Unread from the operator (B1): before this, the only way to learn about a message was to open the chat. -->
      <span v-if="unreadTotal > 0" class="app-support-fab__badge"
            :aria-label="t('support.unread', { n: unreadTotal })">{{ unreadTotal }}</span>
    </button>
    <SupportChatDrawer v-if="!isVitrine" :open="supportOpen" @close="supportOpen = false" />
  </template>

  <!-- App-lock (Phase 4): re-prompt device PIN after inactivity / on foreground while session is alive. -->
  <AppLock v-if="appLock.locked.value" />

  <!-- Step-up fallback chooser — single instance, driven by useStepUp() from any sensitive flow. -->
  <StepUpChooser v-if="auth.isAuthenticated" />

  <!-- "A new version is available" — on every screen, signed in or not. -->
  <AppUpdateBanner />
  <DsOfflineBanner />
</template>

<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { useAuthStore } from '@/stores/auth';
import { useOnboardingStore } from '@/stores/onboarding';
import { useInstallPrompt } from '@/composables/useInstallPrompt';
import { TAB_INTENT } from '@/composables/useTabIntent';
import { useCapabilities } from '@/composables/useCapabilities';
import { useProduct } from '@/product';
import { useSupport } from '@/composables/useSupport';
import { ensureWebPushIfGranted } from '@/utils/webPush';

import AppShell from '@/components/ui/shell/AppShell.vue';
import CabinetShell from '@/components/ds/shell/CabinetShell.vue';
import CabinetNav, { type CabinetNavItem } from '@/components/ds/shell/CabinetNav.vue';
import PublicLayout from '@/components/ds/shell/PublicLayout.vue';
import DsOfflineBanner from '@/components/ds/DsOfflineBanner.vue';
import { useLayout } from '@/composables/useLayout';
import TopHeader from '@/components/ui/shell/TopHeader.vue';
import BottomTabBar, { type TabId } from '@/components/ui/shell/BottomTabBar.vue';
import InstallPwaSheet from '@/components/ui/sheets/InstallPwaSheet.vue';
import SupportChatDrawer from '@/components/support/SupportChatDrawer.vue';
import AppLock from '@/components/AppLock.vue';
import StepUpChooser from '@/components/StepUpChooser.vue';
import AppUpdateBanner from '@/components/ui/feedback/AppUpdateBanner.vue';
import { useAppLock } from '@/composables/useAppLock';
import {
  IconHome,
  IconArrows,
  IconHistory,
  IconChart,
  IconSettings,
  IconUser,
  IconChat,
  IconFamily,
} from '@/components/ui/icons';
import { familyApi } from '@/api/family';
import { setFamilyGroupLabel, useFamilyLabels } from '@/composables/useFamilyGroup';
import { clearWarm } from '@/api/warmCache';
import { warmVitrineProfileTab } from '@/components/settingsProbes';

const auth = useAuthStore();

/** The cabinet's tab screens kept alive (by component name) and their routes, warmed on idle after sign-in. */
const KEPT_TABS = ['VitrineCardView', 'CabinetConciergeView', 'VitrineSettingsView', 'VitrineHistoryView'];
const KEPT_TAB_ROUTES = ['card', 'concierge', 'settings', 'history'];
const keptTabs = computed(() => (auth.isAuthenticated ? KEPT_TABS : []));
// Drives which vitrine tabs are actionable at the client's current onboarding stage.
const onboarding = useOnboardingStore();
const router = useRouter();
const route = useRoute();
const { t } = useI18n();

// App-lock: arm when authenticated (if a device PIN is enrolled), disarm on logout. Lock on a COLD
// open (session restored from cookie at boot) but NOT right after a fresh interactive login.
const appLock = useAppLock();
let firstAuthEval = true;
watch(() => auth.isAuthenticated, v => {
  const coldRestore = firstAuthEval && v;
  firstAuthEval = false;
  if (v) {
    void appLock.init(coldRestore);
    // Re-arm the Web Push subscription on every app open (not just when Settings is opened): a subscription
    // the browser rotated/invalidated (→ 410 → revoked server-side) is replaced with a fresh one here, so
    // push keeps working without the user re-toggling. No-op unless notifications are already granted.
    void ensureWebPushIfGranted();
  } else {
    appLock.reset();
  }
}, { immediate: true });

const { product, isVitrine } = useProduct();
const { navLabel: familyNav } = useFamilyLabels();   // tab label derived from the group type

// Family Office nav entry: shown only when the customer holds ≥1 access grant. Cheap probe (no balances).
const hasFamilyAccess = ref(false);
watch(() => auth.isAuthenticated, async v => {
  if (v) {
    // Probe for BOTH products — vitrine is the primary prod surface, the entry belongs in its tab bar too.
    try { const a = await familyApi.access(); hasFamilyAccess.value = a.hasAccess; setFamilyGroupLabel(a.groupLabel); }
    catch { hasFamilyAccess.value = false; }
  } else {
    hasFamilyAccess.value = false;
  }
}, { immediate: true });

// The tab bar is global, so the onboarding stage must be known even when the client lands somewhere other than
// the card screen (a deep link to Settings or Support) — otherwise those pages offer tabs the guard bounces.
// Declared after useProduct(): `isVitrine` cannot be touched from the auth watcher above, which runs first.
watch(() => [auth.isAuthenticated, isVitrine.value] as const, ([authed, vitrine]) => {
  if (authed && vitrine) void onboarding.load();
}, { immediate: true });

// Capabilities — one shared copy (useCapabilities) for the tab bar and the card / top-up screens (§11.1). Signing out
// drops it, so the next client on this device never sees the previous one's flags. Fail-closed on error.
const capabilities = useCapabilities();
watch(() => [auth.isAuthenticated, isVitrine.value] as const, ([authed, vitrine]) => {
  if (authed && vitrine) void capabilities.load();
  else if (!authed) capabilities.reset();
}, { immediate: true });

// Redesign Wave 0: unified Tacita brand theme across ALL products (vitrine + full) — no more
// "black" full / cream vitrine split. The language is NOT decided here: main.ts picks it once (the client's
// choice, else the browser's) and pushes that same value to the server. A second default here (it used to force
// ru) left the screen Russian while the server — and so the statement, e-mails and pushes — stayed on the
// browser's language.
watch(product, () => {
  document.documentElement.setAttribute('data-theme', 'tacita');
}, { immediate: true });

const fullTabs = computed(() => {
  const items = [
    { id: 'home'      as TabId, label: t('nav.home'),     icon: IconHome,    route: 'home' },
    { id: 'payments'  as TabId, label: t('nav.payments'), icon: IconArrows,  route: 'payments' },
    { id: 'history'   as TabId, label: t('nav.history'),  icon: IconHistory, route: 'history' },
    { id: 'analytics' as TabId, label: t('nav.analytics'),icon: IconChart,   route: 'analytics' },
  ];
  // Family Office tab — only when the client holds a grant, before Settings.
  if (hasFamilyAccess.value) {
    items.push({ id: 'family' as TabId, label: familyNav.value, icon: IconUser, route: 'family' });
  }
  items.push({ id: 'settings' as TabId, label: t('nav.settings'), icon: IconSettings, route: 'settings' });
  return items;
});
// The full product's tab bar; the client cabinet has its own navigation (CabinetNav).
const tabs = fullTabs;

const routeToTab: Record<string, TabId> = {
  card: 'card',
  home: 'home',
  payments: 'payments',
  topup: 'payments',        // the vitrine Transfers tab opens the top-up flow → keep that tab active
  'concierge-request': 'payments',
  history: 'history',
  analytics: 'analytics',
  settings: 'settings',
  family: 'family',
  'family-member-tx': 'family',
  'family-export': 'family',
  // Legacy wallet flow — keep accessible as settings sub-area until Sprint B7.
  wallet: 'settings',
  'wallet-receive': 'settings',
  'wallet-send': 'settings',
  'wallet-pending': 'settings',
  profile: 'settings',
  kyc: 'settings',
  cards: 'home',
  'card-new': 'home',
  'card-detail': 'home',
  'card-issued': 'home',
};

const currentTab = computed<TabId>(() => {
  const name = String(route.name ?? '');
  // /support — a pushed screen: no tab is active (the 'support' id won't match any tab).
  if (name === 'support') return 'support' as TabId;
  return routeToTab[name] ?? (isVitrine.value ? 'card' : 'home');
});

// Global support FAB. Hidden on public routes (login/magic/etc).
// supportOpen is shared (useSupport) so it can be opened from the manager-chip / "Top up" / Transfers tab.
const { supportOpen, unreadTotal, unreadAll, startUnreadPolling, stopUnreadPolling } = useSupport();
const routeIsPublic = computed(() => Boolean(route.meta?.public));
const usePublicLayout = computed(() => routeIsPublic.value && !(auth.isAuthenticated && route.name === 'privacy'));
/** Which shell frames the page — the root-level sheets re-mount with it to reach its #sheet-portal. */
const shellKey = computed(() => (usePublicLayout.value ? 'public' : isVitrine.value ? 'cabinet' : 'app'));

// The unread counter (B1) is polled at the shell level: the badge is needed on screens that have no chat —
// otherwise the client only learns of an operator message by opening the chat, which is what the badge is for.
// Its own watcher rather than the auth watcher above: that one is declared BEFORE useSupport() and cannot reach
// it (the same reason isVitrine has a separate watcher).
watch(() => auth.isAuthenticated, authed => {
  if (authed) startUnreadPolling();
  else stopUnreadPolling();
}, { immediate: true });

const headerTitle = computed(() => {
  switch (currentTab.value) {
    case 'home':      return t('nav.home');
    case 'payments':  return t('nav.payments');
    case 'history':   return t('nav.history');
    case 'analytics': return t('nav.analytics');
    case 'settings':  return t('settings.title');
    default:          return t('app.name');
  }
});

const headerSubtitle = computed<string | undefined>(() => {
  switch (currentTab.value) {
    case 'payments': return t('payments.subtitle');
    case 'history':  return t('history.subtitle');
    default:         return undefined;
  }
});

async function onTabChange(id: TabId) {
  const target = tabs.value.find(x => x.id === id);
  // `fromTab` — отметка осознанного нажатия вкладки. Домашний экран умеет сам открывать незавершённый
  // денежный шаг (VitrineCardView: watch(journeyStep)), и это правильно при входе по ссылке или холодном
  // старте. Но при нажатии «Главный» тот же переход возвращал клиента обратно в заявку — вкладка выглядела
  // сломанной (08.09.2026). Метка едет в history.state и позволяет отличить «пришли сами» от «клиент
  // попросил именно Home»; экран её считывает и гасит, чтобы она не пережила перезагрузку страницы.
  if (target) {
    router.push({ name: target.route, state: { fromTab: true } })
      .catch(() => { /* nav aborted/duplicate — ignore */ });
  }
}

function goSettings() { router.push({ name: 'settings' }); }

// ── Client cabinet navigation (docs/cabinet-redesign/02_ЭКРАНЫ_БЫЛО_СТАЛО.md §1) ─────────────────────────────────
// Главная · Консьерж · Профиль on a phone; the rail and the sidebar add «Семейный офис» and read «Настройки».
const layout = useLayout();

const cabinetNav = computed<CabinetNavItem[]>(() => {
  const phone = layout.isPhone.value;
  const items: CabinetNavItem[] = [
    { id: 'card', label: t('nav.home'), icon: IconHome },
    { id: 'concierge', label: t('nav.concierge'), icon: IconChat, dot: unreadAll.value > 0 },
  ];
  if (!phone && hasFamilyAccess.value) items.push({ id: 'family', label: t('nav.familyOffice'), icon: IconFamily });
  items.push({ id: 'settings', label: phone ? t('nav.profile') : t('nav.settings'), icon: phone ? IconUser : IconSettings });
  return items;
});

/** Which navigation item a route belongs to — screens without their own item light up their parent (02 §1). */
const cabinetRouteToItem: Record<string, string> = {
  card: 'card',
  'card-issuance': 'card',
  'card-fund': 'card',
  topup: 'card',
  history: 'card',
  concierge: 'concierge',
  payments: 'concierge',
  'concierge-request': 'concierge',
  support: 'concierge',
  settings: 'settings',
  help: 'settings',
  family: 'family',
  'family-member-tx': 'family',
  'family-export': 'family',
};

const cabinetCurrent = computed(() => {
  const item = cabinetRouteToItem[String(route.name ?? '')] ?? 'card';
  // The family office has its own item only beside the content; on a phone it lives under the profile.
  return item === 'family' && layout.isPhone.value ? 'settings' : item;
});

/** Concierge has no route of its own before stage E2 — until then its item opens the existing support screen. */
function conciergeRouteName(): string {
  return router.hasRoute('concierge') ? 'concierge' : 'support';
}

function onCabinetSelect(id: string) {
  const name = id === 'concierge' ? conciergeRouteName() : id;
  // TAB_INTENT: Home and top-up tell «the client pressed it» from «arrived by a link» by this mark (bug 08.09).
  router.push({ name, state: { ...TAB_INTENT } }).catch(() => { /* aborted / duplicate navigation */ });
}

// Install prompt — shown 5s after the first authenticated page load if not already standalone.
const install = useInstallPrompt();
// Shared with the composable so the Settings row can raise the same sheet (a local ref could not be reached).
const installSheetOpen = install.sheetOpen;

/** Key for the second, post-issuance showing — once per client, never on every visit to the card. */
const INSTALL_SHOWN_ON_ISSUED = 'tacita.install.shownOnIssued';

function maybeShowInstallSheet(ignoreSnooze = false) {
  // Desktop has no install for us — no sheet, no settings row, nothing. Windows and macOS used to get the
  // sheet anyway, and its "Install" button was mute there, which is where this whole defect started.
  if (!install.isMobile.value) return;
  if (!auth.isAuthenticated) return;
  if (install.isStandalone.value) return;
  // The 7-day snooze applies to the FIRST showing. The second one is tied to an event worth interrupting for —
  // the card has just been issued — so it deliberately ignores the snooze.
  if (!ignoreSnooze && !install.shouldShow()) return;
  // Defer 5 s so customer sees real UI first.
  window.setTimeout(() => {
    if (auth.isAuthenticated && !install.isStandalone.value) {
      installSheetOpen.value = true;
    }
  }, 5000);
}

function onInstallClose() {
  install.snooze();
  installSheetOpen.value = false;
}

async function onInstallTrigger() {
  const outcome = await install.triggerNativePrompt();
  // 'unavailable' means there is no system prompt to show — closing silently is precisely the mute click this
  // defect was reported as. Leave the sheet up (on iOS it carries the manual steps, which are the real answer).
  if (outcome === 'unavailable') return;
  installSheetOpen.value = false;
}

onMounted(() => maybeShowInstallSheet());

/** Loads the tabs' code while the client looks at the first screen: the first tap then waits for data only. */
function prefetchCabinetTabs() {
  const idle: (f: () => void) => void = (window as any).requestIdleCallback ?? ((f: () => void) => setTimeout(f, 1500));
  idle(() => {
    for (const name of KEPT_TAB_ROUTES) {
      const load = router.getRoutes().find(r => r.name === name)?.components?.default;
      if (typeof load === 'function') void (load as () => Promise<unknown>)().catch(() => { /* a later tap retries */ });
    }
    // …and the profile tab's reads (nine requests in two rounds): its first visit then draws at once.
    if (auth.customerId) warmVitrineProfileTab(auth.customerId);
  });
}
// The warmed reads belong to one client: sign-out and another client signing in drop them.
watch(() => auth.customerId, () => clearWarm());
watch(() => isVitrine.value && auth.isAuthenticated, on => { if (on) prefetchCabinetTabs(); }, { immediate: true });
watch(() => auth.isAuthenticated, (logged) => {
  if (logged) maybeShowInstallSheet();
});

/**
 * Second reason to offer the install: onboarding is over and the card has just appeared.
 *
 * Three conditions, and the middle one is the point: a client who already tapped "Install" once has made their
 * decision, and we do not ask again.
 */
watch(() => onboarding.stage, (now, before) => {
  if (now !== 'ISSUED' || before === 'ISSUED') return;        // the card appeared just now
  if (install.installAttempted()) return;                     // they already decided the first time
  try {
    if (window.localStorage.getItem(INSTALL_SHOWN_ON_ISSUED)) return;   // this showing already happened
    window.localStorage.setItem(INSTALL_SHOWN_ON_ISSUED, '1');
  } catch { /* private mode — one extra showing is cheaper than losing the install */ }
  maybeShowInstallSheet(true);
});
</script>

<style scoped lang="scss">
/* Global support FAB, fixed above the bottom tab bar.
   Desktop-frame: align the FAB to the 480px app-frame's right edge on wide viewports;
   on mobile it keeps the previous behaviour (18px from the viewport edge). */
.app-support-fab {
  position: fixed;
  right: calc(max(0px, (100vw - 480px) / 2) + 18px);
  bottom: calc(var(--tab-bar-height, 96px) + env(safe-area-inset-bottom));
  width: 52px; height: 52px;
  border-radius: 50%;
  background: var(--gold);
  color: var(--text-inverse);
  border: none;
  font-size: 22px; font-weight: 600;
  box-shadow: 0 8px 24px -8px rgba(0, 0, 0, 0.45);
  cursor: pointer;
  z-index: 100;
}
/* Same look as the badge in the chat header (SupportChatDrawer) — one meaning should have one appearance. */
.app-support-fab__badge {
  position: absolute; top: -2px; right: -2px;
  display: inline-flex; align-items: center; justify-content: center;
  min-width: 22px; height: 22px; padding: 0 6px;
  border-radius: 11px;
  background: var(--red, #c8615a);
  color: #fff;
  font-family: 'Inter', sans-serif;
  font-size: 11px; font-weight: 600;
}
</style>
